var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ObjectSerializer } from './models/models/model_serializer';
import { SSEResultState } from './models';
import { fhLog } from './feature_hub_config';
export class PollingBase {
    constructor(url, frequency, callback) {
        this.stopped = false;
        this.url = url;
        this.frequency = frequency;
        this._callback = callback;
    }
    attributeHeader(header) {
        this._header = header;
        return this.poll();
    }
    stop() {
        this.stopped = true;
    }
    delayTimer() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise(((resolve, reject) => {
                if (!this.stopped && this.frequency > 0) {
                    setTimeout(() => this.poll().then(resolve).catch(reject), this.frequency);
                }
                else {
                    resolve();
                }
            }));
        });
    }
}
class BrowserPollingService extends PollingBase {
    constructor(options, url, frequency, callback) {
        super(url, frequency, callback);
        this._options = options;
    }
    poll() {
        return new Promise((resolve, reject) => {
            const req = new XMLHttpRequest();
            req.open('GET', this.url);
            req.setRequestHeader('Content-type', 'application/json');
            if (this._etag) {
                req.setRequestHeader('if-none-match', this._etag);
            }
            if (this._header) {
                req.setRequestHeader('x-featurehub', this._header);
            }
            req.send();
            req.onreadystatechange = () => {
                if (req.readyState === 4) {
                    if (req.status === 200) {
                        this._etag = req.getResponseHeader('etag');
                        this._callback(ObjectSerializer.deserialize(JSON.parse(req.responseText), 'Array<Environment>'));
                        resolve();
                    }
                    else if (req.status == 304) {
                        resolve();
                    }
                    else if (req.status >= 400 && req.status < 500) {
                        reject(`Failed to connect to ${this.url} with ${req.status}`);
                    }
                    else {
                        this.delayTimer().then(resolve).catch(reject);
                    }
                }
            };
        });
    }
}
export class FeatureHubPollingClient {
    constructor(repository, config, frequency, options = {}) {
        this._pollingStarted = false;
        this._frequency = frequency;
        this._repository = repository;
        this._options = options;
        this._config = config;
        this._url = config.getHost() + 'features?' + config.getApiKeys().map(e => 'sdkUrl=' + encodeURIComponent(e)).join('&');
    }
    _initService() {
        if (this._pollingService === undefined) {
            this._pollingService =
                FeatureHubPollingClient.pollingClientProvider(this._options, this._url, this._frequency, (e) => this.response(e));
            fhLog.log(`featurehub: initialized polling client to ${this._url}`);
        }
    }
    contextChange(header) {
        if (!this._config.clientEvaluated()) {
            if (this._xHeader !== header) {
                this._xHeader = header;
                this._initService();
                const pollForContext = this._pollingService.attributeHeader(header);
                if (!this._pollingStarted) {
                    this._restartTimer();
                }
                return pollForContext;
            }
        }
        else {
            return new Promise((resolve) => resolve());
        }
    }
    clientEvaluated() {
        return this._config.clientEvaluated();
    }
    requiresReplacementOnHeaderChange() {
        return false;
    }
    close() {
        if (this._pollingService) {
            this._pollingService.stop();
        }
    }
    poll() {
        if (this._pollPromiseResolve !== undefined || this._pollingStarted) {
            return new Promise((resolve) => resolve());
        }
        this._initService();
        return new Promise((resolve, reject) => {
            this._pollPromiseReject = reject;
            this._pollPromiseResolve = resolve;
            this._restartTimer();
        });
    }
    stop() {
        this._pollingService.stop();
        this._pollingService = undefined;
    }
    _restartTimer() {
        this._pollingStarted = true;
        setTimeout(() => this._pollingService.poll()
            .then(() => {
            if (this._pollPromiseResolve !== undefined) {
                this._pollPromiseResolve();
            }
        })
            .catch((e) => {
            fhLog.error(`Failed to poll, restarting in ${this._frequency}ms: ${e}`);
            this._repository.notify(SSEResultState.Failure, null);
            if (this._pollPromiseReject !== undefined) {
                this._pollPromiseReject();
            }
        }).finally(() => {
            this._pollPromiseReject = undefined;
            this._pollPromiseResolve = undefined;
            this._restartTimer();
        }), this._frequency);
    }
    response(environments) {
        if (environments.length === 0) {
            this._startable = false;
            this.stop();
            this._repository.notify(SSEResultState.Failure, null);
        }
        else {
            const features = new Array();
            environments.forEach(e => {
                if (e.features.length > 0) {
                    e.features.forEach(f => {
                        f.environmentId = e.id;
                    });
                    features.push(...e.features);
                }
            });
            this._repository.notify(SSEResultState.Features, features);
        }
    }
}
FeatureHubPollingClient.pollingClientProvider = (opt, url, freq, callback) => new BrowserPollingService(opt, url, freq, callback);
//# sourceMappingURL=polling_sdk.js.map